import { Autocomplete, Box, Button, Card, CircularProgress, MenuItem, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useChatPdVContext } from "../../../../contexts/atendimento/ChatPdVContext";
import { useClientes } from "../../../../../clientes/hooks/useClientes";
import { useRegex } from "../../../../../core/hooks/useRegex";
import { useMascara } from "../../../../../core/hooks/useMascara";
import { ICliente } from "../../../../../clientes/interface/ICliente";
import { Person, PersonSearch, PersonAdd } from "@mui/icons-material";
import { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import { acaoPrincipal, contorno, cores, limitarLinhas, raio, superficie, texto } from "../../identidade";

const cardSx = {
    p: 2,
    borderRadius: raio.md,
    border: (theme: Theme) => contorno(theme, 0.08),
    backgroundColor: (theme: Theme) => superficie(theme, 0.03),
};

/** O cliente que a IA já identificou pelo telefone é o caminho mais provável. */
const cardDestaqueSx = {
    p: 2,
    borderRadius: raio.md,
    border: `1px solid ${alpha(cores.marca[500], 0.35)}`,
    backgroundColor: alpha(cores.marca[500], 0.06),
};

const buttonSx = (theme: Theme) => ({
    ...acaoPrincipal(theme),
    mt: 1.75,
    fontSize: "12.5px",
    py: 0.95,
    borderRadius: raio.sm,
});

/**
 * Campo sobre card: o fundo é o papel do tema, não um overlay. Com overlay o
 * campo ficava do mesmo tom do card e sumia dentro dele.
 */
const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: raio.sm,
        backgroundColor: (theme: Theme) => theme.palette.background.paper,
    },
};

export const ChatPdVSelecionarCliente = () => {
    const {
        contatoChat,
        clienteIdentificado,
        vincularClienteNoChat,
        cadastrarEVincularCliente,
        loadingCliente
    } = useChatPdVContext();

    const { buscarClientesPorNomeOuTelefone } = useClientes();
    const { testeNumericoTelefone } = useRegex();
    const { mascaraTelefone, formatarTelefoneParaPadrao } = useMascara();

    // State para busca de cliente existente
    const [clientesBusca, setClientesBusca] = useState<ICliente[]>([]);
    const [clienteSelecionado, setClienteSelecionado] = useState<ICliente | undefined>(undefined);
    const [filtroTipo, setFiltroTipo] = useState<"nome" | "telefone">("nome");
    const [filtro, setFiltro] = useState("");
    const [inputBusca, setInputBusca] = useState("");

    // State para cadastro rápido
    const [nomeCadastro, setNomeCadastro] = useState("");
    const [telefoneCadastro, setTelefoneCadastro] = useState("");

    useEffect(() => {
        setNomeCadastro(contatoChat?.nome || "");
        setTelefoneCadastro(contatoChat?.telefone ? formatarTelefoneParaPadrao(contatoChat.telefone) || "" : "");
    }, [contatoChat]);

    const handleInputChange = async (_event: React.SyntheticEvent, valor: string, reason: string) => {
        if (reason === "reset") {
            setInputBusca(valor);
            return;
        }

        let valorTratado = valor;
        let novoFiltroTipo: "nome" | "telefone" = "nome";

        if (testeNumericoTelefone(valor) && valor.length > 0) {
            valorTratado = mascaraTelefone(valor);
            novoFiltroTipo = "telefone";
        }

        setInputBusca(valorTratado);
        setFiltro(valorTratado.toLowerCase());
        setFiltroTipo(novoFiltroTipo);

        if (!valorTratado.length) {
            setClientesBusca([]);
            return;
        }

        const clientes = await buscarClientesPorNomeOuTelefone(valorTratado, novoFiltroTipo);
        const clientesEncontrados = clientes === null ? [] : clientes;
        setClientesBusca(clientesEncontrados);
    };

    const handleClienteChange = (_event: React.SyntheticEvent, value: ICliente | null) => {
        setClienteSelecionado(value || undefined);
    };

    const handleVincularSelecionado = () => {
        if (clienteSelecionado) {
            vincularClienteNoChat(clienteSelecionado);
        }
    };

    const handleCadastrarEVincular = () => {
        if (nomeCadastro.trim()) {
            cadastrarEVincularCliente(nomeCadastro.trim(), telefoneCadastro.trim());
        }
    };

    if (loadingCliente) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Typography sx={{ ...texto.secao, color: "text.secondary" }}>
                Vincular cliente
            </Typography>

            {/* Card 1: Cliente identificado (apenas se encontrado) */}
            {clienteIdentificado && (
                <Card sx={cardDestaqueSx} elevation={0}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
                        <Person sx={{ fontSize: 16, color: cores.marca[500] }} />
                        <Typography sx={{ ...texto.etiqueta, color: cores.marca[500], textTransform: "uppercase" }}>
                            Identificado pelo telefone
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "14px", fontWeight: 800, ...limitarLinhas(2) }}>
                        {clienteIdentificado.nome}
                    </Typography>
                    {clienteIdentificado.telefone && (
                        <Typography sx={{ ...texto.apoio, color: "text.secondary", mt: 0.25 }}>
                            {clienteIdentificado.telefone}
                        </Typography>
                    )}
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() => vincularClienteNoChat(clienteIdentificado)}
                        sx={buttonSx}
                    >
                        Vincular
                    </Button>
                </Card>
            )}

            {/* Card 2: Vincular cliente existente (busca) */}
            <Card sx={cardSx} elevation={0}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <PersonSearch sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography sx={{ ...texto.secao, color: "text.secondary" }}>
                        Buscar cliente
                    </Typography>
                </Box>
                <Autocomplete
                    options={clientesBusca}
                    filterOptions={(x) => x}
                    slotProps={{ popper: { sx: { zIndex: 10002 } } }}
                    inputValue={inputBusca}
                    onInputChange={handleInputChange}
                    getOptionLabel={(cliente) => {
                        if (filtroTipo === "telefone" && cliente.telefone) {
                            return cliente.telefone;
                        }
                        if (cliente.nome?.toLowerCase().includes(filtro)) {
                            return cliente.nome;
                        }
                        if (cliente.observacao?.toLowerCase().includes(filtro)) {
                            return cliente.observacao;
                        }
                        return cliente.nome || "";
                    }}
                    onChange={handleClienteChange}
                    size="small"
                    noOptionsText="Nenhum cliente encontrado"
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Nome, telefone ou observação"
                            size="small"
                            sx={inputSx}
                        />
                    )}
                    renderOption={(props, option) => (
                        <MenuItem {...props} key={option.uuid} value={option.uuid}>
                            {option.nome} {option.observacao ? ` ${option.observacao}` : ""}{" "}
                            {option.telefone ? `- ${option.telefone}` : ""}
                        </MenuItem>
                    )}
                />
                <Button
                    variant="contained"
                    fullWidth
                    onClick={handleVincularSelecionado}
                    disabled={!clienteSelecionado}
                    sx={buttonSx}
                >
                    Vincular
                </Button>
            </Card>

            {/* Card 3: Cadastrar novo cliente (rápido) */}
            <Card sx={cardSx} elevation={0}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <PersonAdd sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography sx={{ ...texto.secao, color: "text.secondary" }}>
                        Cadastrar novo cliente
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <TextField
                        label="Nome"
                        value={nomeCadastro}
                        onChange={(e) => setNomeCadastro(e.target.value)}
                        size="small"
                        fullWidth
                        sx={inputSx}
                    />
                    <TextField
                        label="Telefone"
                        value={telefoneCadastro}
                        onChange={(e) => setTelefoneCadastro(mascaraTelefone(e.target.value))}
                        size="small"
                        fullWidth
                        sx={inputSx}
                    />
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={handleCadastrarEVincular}
                        disabled={!nomeCadastro.trim()}
                        sx={buttonSx}
                    >
                        Cadastrar e vincular
                    </Button>
                </Box>
            </Card>
        </Box>
    );
};
